import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { MultiImageUpload, ImageUpload } from "../components/ui/image-upload";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
// Admin Tab Components
import MarketingTab from "../components/admin/MarketingTab";
import CertificatesTab from "../components/admin/CertificatesTab";
import ProductsTab from "../components/admin/ProductsTab";
import OrdersTab from "../components/admin/OrdersTab";
import ArticlesTab from "../components/admin/ArticlesTab";
import SalesAgentsTab from "../components/admin/SalesAgentsTab";
import WholesaleTab from "../components/admin/WholesaleTab";
import DynamicPricingTab from "../components/admin/DynamicPricingTab";
import AmbassadorManagementTab from "../components/admin/AmbassadorManagementTab";
import FramePricingTab from "../components/admin/FramePricingTab";
import FrameBuilderTab from "../components/admin/FrameBuilderTab";
import AuctionsTab from "../components/admin/AuctionsTab";
import AIAssistant from "../components/admin/AIAssistant";
import AIFloatingWidget from "../components/admin/AIFloatingWidget";
import QuickStartGuide from "../components/admin/QuickStartGuide";
import WelcomeModal from "../components/admin/WelcomeModal";
import SiteSettingsTab from "../components/admin/SiteSettingsTab";
import OverviewDashboard from "../components/admin/OverviewDashboard";
import UsersManagementTab from "../components/admin/UsersManagementTab";
import AnalyticsDashboard from "../components/admin/AnalyticsDashboard";
import SocialMediaTab from "../components/admin/SocialMediaTab";
import InquiriesTab from "../components/admin/InquiriesTab";
import { toast } from "sonner";
import { 
  Package, Users, ShoppingCart, DollarSign, 
  CheckCircle, XCircle, Clock, Edit, Trash2,
  Plus, Eye, Building, FileText, Send, MessageSquare,
  Image, X, Briefcase, MapPin, Megaphone, TrendingUp,
  Share2, Award, Settings, Percent, Newspaper, Layout,
  Save, RotateCcw, BookOpen, Globe, Type, Upload, Calendar,
  Bot, Shield, Rocket, UserPlus, Frame, Settings2, Gavel, BarChart3
} from "lucide-react";
import { API } from "../apiConfig";


const AdminDashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [wholesaleApps, setWholesaleApps] = useState([]);
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [salesAgents, setSalesAgents] = useState([]);
  const [promoters, setPromoters] = useState([]);
  const [commissionSettings, setCommissionSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Territory Assignment Dialog
  const [territoryDialogOpen, setTerritoryDialogOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [territoryForm, setTerritoryForm] = useState({ territory: "", postcodes: "" });
  
  // Product Editor State
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    title: "",
    description: "",
    base_price: "",
    category: "limited_edition",
    images: [""],
    stock: 100,
    is_limited_edition: true,
    is_featured: true,
    has_variants: true,
    wholesale_min_qty: 1,
    variants: [
      { name: "Small", size: "A4", price: 99, base_price: 99, stock: 100, wholesale_min_qty: 4 },
      { name: "Medium", size: "A3", price: 179, base_price: 179, stock: 75, wholesale_min_qty: 3 },
      { name: "Large", size: "A2", price: 299, base_price: 299, stock: 50, wholesale_min_qty: 1 }
    ]
  });

  // Quote Response State
  const [quoteResponseOpen, setQuoteResponseOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteResponse, setQuoteResponse] = useState({ discount_percent: 20, message: "" });

  // Articles State
  const [articles, setArticles] = useState([]);
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: "",
    excerpt: "",
    content: "",
    category: "news",
    featured_image: "",
    tags: "",
    is_published: true,
    is_featured: false
  });

  // CMS State
  const [cmsContent, setCmsContent] = useState({});
  const [cmsEditMode, setCmsEditMode] = useState({});
  const [cmsEditValues, setCmsEditValues] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/");
      return;
    }
    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const [statsRes, ordersRes, productsRes, usersRes, wholesaleRes, quotesRes, agentsRes, commissionRes, articlesRes, cmsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/orders`, { headers }),
        axios.get(`${API}/products`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/wholesale-applications`, { headers }),
        axios.get(`${API}/admin/quote-requests`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/sales-agents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/commission-settings`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/articles`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/cms`, { headers }).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setOrders(ordersRes.data);
      setProducts(productsRes.data);
      setUsers(usersRes.data);
      setWholesaleApps(wholesaleRes.data);
      setQuoteRequests(quotesRes.data);
      setSalesAgents(agentsRes.data);
      setCommissionSettings(commissionRes.data);
      setArticles(articlesRes.data);
      setCmsContent(cmsRes.data);
      // Filter promoters from users
      setPromoters(usersRes.data.filter(u => u.role === "promoter"));
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', { 
      year: 'numeric', month: 'short', day: 'numeric' 
    });
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}`, { status }, { headers });
      setOrders(orders.map(o => o.order_id === orderId ? { ...o, status } : o));
      toast.success("Order status updated");
    } catch (error) {
      toast.error("Failed to update order");
    }
  };

  const approveWholesale = async (userId, discount) => {
    try {
      await axios.put(`${API}/admin/wholesale/${userId}/approve?discount=${discount}`, {}, { headers });
      setWholesaleApps(wholesaleApps.filter(a => a.user_id !== userId));
      toast.success(`Wholesale account approved with ${discount * 100}% discount`);
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  const rejectWholesale = async (userId) => {
    try {
      await axios.put(`${API}/admin/wholesale/${userId}/reject`, {}, { headers });
      setWholesaleApps(wholesaleApps.filter(a => a.user_id !== userId));
      toast.success("Application rejected");
    } catch (error) {
      toast.error("Failed to reject");
    }
  };

  // Product Management Functions
  const openProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        title: product.title,
        description: product.description,
        base_price: product.base_price || product.price,
        category: product.category,
        images: product.images?.length ? product.images : [""],
        stock: product.stock,
        is_limited_edition: product.is_limited_edition || false,
        is_featured: product.is_featured || false,
        has_variants: product.has_variants || false,
        wholesale_min_qty: product.wholesale_min_qty || 1,
        variants: product.variants?.length ? product.variants : [
          { name: "Small", size: "A4", price: 99, base_price: 99, stock: 100, wholesale_min_qty: 4 }
        ]
      });
    } else {
      setEditingProduct(null);
      setProductForm({
        title: "",
        description: "",
        base_price: "",
        category: "limited_edition",
        images: [""],
        stock: 100,
        is_limited_edition: true,
        is_featured: true,
        has_variants: true,
        wholesale_min_qty: 1,
        variants: [
          { name: "Small", size: "A4", price: 99, base_price: 99, stock: 100, wholesale_min_qty: 4 },
          { name: "Medium", size: "A3", price: 179, base_price: 179, stock: 75, wholesale_min_qty: 3 },
          { name: "Large", size: "A2", price: 299, base_price: 299, stock: 50, wholesale_min_qty: 1 }
        ]
      });
    }
    setProductDialogOpen(true);
  };

  const handleProductSubmit = async () => {
    try {
      // Get artist ID
      const artistRes = await axios.get(`${API}/artists`);
      const artistId = artistRes.data[0]?.artist_id;

      const productData = {
        ...productForm,
        base_price: parseFloat(productForm.base_price),
        price: parseFloat(productForm.base_price),
        artist_id: artistId,
        images: productForm.images.filter(img => img.trim()),
        variants: productForm.has_variants ? productForm.variants.map(v => ({
          ...v,
          variant_id: v.variant_id || `var_${v.name.toLowerCase()}`,
          price: parseFloat(v.price),
          base_price: parseFloat(v.base_price || v.price),
          stock: parseInt(v.stock),
          original_stock: parseInt(v.stock),
          wholesale_min_qty: parseInt(v.wholesale_min_qty)
        })) : []
      };

      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.product_id}`, productData, { headers });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/products`, productData, { headers });
        toast.success("Product created");
      }
      
      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Product save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/admin/products/${productId}`, { headers });
      setProducts(products.filter(p => p.product_id !== productId));
      toast.success("Product deleted");
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const addVariant = () => {
    setProductForm({
      ...productForm,
      variants: [...productForm.variants, { 
        name: "", size: "", price: 0, base_price: 0, stock: 50, wholesale_min_qty: 1 
      }]
    });
  };

  const removeVariant = (index) => {
    setProductForm({
      ...productForm,
      variants: productForm.variants.filter((_, i) => i !== index)
    });
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...productForm.variants];
    newVariants[index] = { ...newVariants[index], [field]: value };
    setProductForm({ ...productForm, variants: newVariants });
  };

  const addImageField = () => {
    setProductForm({ ...productForm, images: [...productForm.images, ""] });
  };

  const updateImage = (index, value) => {
    const newImages = [...productForm.images];
    newImages[index] = value;
    setProductForm({ ...productForm, images: newImages });
  };

  const removeImage = (index) => {
    setProductForm({
      ...productForm,
      images: productForm.images.filter((_, i) => i !== index)
    });
  };

  // Quote Response Functions
  const openQuoteResponse = (quote) => {
    setSelectedQuote(quote);
    setQuoteResponse({ discount_percent: 20, message: "" });
    setQuoteResponseOpen(true);
  };

  const sendQuoteResponse = async () => {
    try {
      await axios.put(`${API}/admin/quote-requests/${selectedQuote.quote_id}/respond`, {
        discount_percent: quoteResponse.discount_percent,
        message: quoteResponse.message,
        status: "responded"
      }, { headers });
      toast.success("Quote response sent");
      setQuoteResponseOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to send response");
    }
  };

  // Sales Agent Functions
  const handleApproveAgent = async (agentId, territory = "") => {
    try {
      await axios.put(`${API}/admin/sales-agents/${agentId}/approve?territory=${territory}`, {}, { headers });
      toast.success("Sales agent approved");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve agent");
    }
  };

  const handleSuspendAgent = async (agentId) => {
    try {
      await axios.put(`${API}/admin/sales-agents/${agentId}/suspend`, {}, { headers });
      toast.success("Sales agent suspended");
      fetchData();
    } catch (error) {
      toast.error("Failed to suspend agent");
    }
  };

  const openTerritoryDialog = (agent) => {
    setSelectedAgent(agent);
    setTerritoryForm({ territory: agent.territory || "", postcodes: (agent.territory_postcodes || []).join(", ") });
    setTerritoryDialogOpen(true);
  };

  const handleAssignTerritory = async () => {
    if (!selectedAgent) return;
    try {
      const postcodes = territoryForm.postcodes.split(",").map(p => p.trim()).filter(p => p);
      await axios.put(
        `${API}/admin/sales-agents/${selectedAgent.agent_id}/territory?territory=${territoryForm.territory}`,
        postcodes,
        { headers }
      );
      toast.success("Territory assigned");
      setTerritoryDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to assign territory");
    }
  };

  const handleUpdateCommission = async (category, rate) => {
    try {
      await axios.put(`${API}/admin/commission-settings/${category}?rate=${rate}`, {}, { headers });
      toast.success(`Commission rate for ${category} updated`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update commission");
    }
  };

  // Article Functions
  const openArticleDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title,
        excerpt: article.excerpt,
        content: article.content,
        category: article.category,
        featured_image: article.featured_image || "",
        tags: (article.tags || []).join(", "),
        is_published: article.is_published,
        is_featured: article.is_featured
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: "",
        excerpt: "",
        content: "",
        category: "news",
        featured_image: "",
        tags: "",
        is_published: true,
        is_featured: false
      });
    }
    setArticleDialogOpen(true);
  };

  const handleArticleSubmit = async () => {
    try {
      const articleData = {
        ...articleForm,
        tags: articleForm.tags.split(",").map(t => t.trim()).filter(t => t)
      };

      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.article_id}`, articleData, { headers });
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, articleData, { headers });
        toast.success("Article created");
      }
      
      setArticleDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Article save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save article");
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm("Are you sure you want to delete this article?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`, { headers });
      toast.success("Article deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  // CMS Functions
  const startCmsEdit = (page, section, currentContent) => {
    setCmsEditMode({ ...cmsEditMode, [`${page}_${section}`]: true });
    setCmsEditValues({ ...cmsEditValues, [`${page}_${section}`]: currentContent });
  };

  const cancelCmsEdit = (page, section) => {
    setCmsEditMode({ ...cmsEditMode, [`${page}_${section}`]: false });
  };

  const saveCmsContent = async (page, section) => {
    try {
      const content = cmsEditValues[`${page}_${section}`];
      await axios.put(`${API}/admin/cms/${page}/${section}`, { content }, { headers });
      toast.success("Content saved");
      setCmsEditMode({ ...cmsEditMode, [`${page}_${section}`]: false });
      fetchData();
    } catch (error) {
      toast.error("Failed to save content");
    }
  };

  const resetCmsContent = async (page, section) => {
    if (!window.confirm("Reset to default content?")) return;
    try {
      await axios.delete(`${API}/admin/cms/${page}/${section}`, { headers });
      toast.success("Content reset to default");
      fetchData();
    } catch (error) {
      toast.error("Failed to reset content");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="pt-32 pb-24 px-6 md:px-12">
          <div className="max-w-7xl mx-auto animate-pulse space-y-6">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="grid grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => <div key={i} className="h-32 bg-muted rounded-2xl" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <Navigation />
      
      <div className="pt-32 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl font-medium mb-2">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage your gallery, products, and wholesale accounts</p>
          </motion.div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
            <TabsList className="bg-muted/50 p-1 rounded-xl flex-wrap">
              <TabsTrigger value="overview" className="rounded-lg">Overview</TabsTrigger>
              <TabsTrigger value="analytics" className="rounded-lg gap-1">
                <BarChart3 className="h-4 w-4" />
                Analytics
              </TabsTrigger>
              <TabsTrigger value="products" className="rounded-lg">Products</TabsTrigger>
              <TabsTrigger value="pricing" className="rounded-lg gap-1">
                <TrendingUp className="h-4 w-4" />
                Pricing
              </TabsTrigger>
              <TabsTrigger value="orders" className="rounded-lg">Orders</TabsTrigger>
              <TabsTrigger value="quotes" className="rounded-lg">
                Quotes
                {quoteRequests.filter(q => q.status === "pending").length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {quoteRequests.filter(q => q.status === "pending").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="wholesale" className="rounded-lg">
                Wholesale
                {wholesaleApps.length > 0 && (
                  <Badge variant="destructive" className="ml-2">{wholesaleApps.length}</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="agents" className="rounded-lg">
                Agents
                {salesAgents.filter(a => a.status === "pending").length > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {salesAgents.filter(a => a.status === "pending").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="promoters" className="rounded-lg">
                Ambassadors
                <Badge variant="outline" className="ml-2">{promoters.length}</Badge>
              </TabsTrigger>
              <TabsTrigger value="users" className="rounded-lg">Users</TabsTrigger>
              <TabsTrigger value="marketing" className="rounded-lg">
                <Calendar className="h-4 w-4 mr-1" />
                Marketing
              </TabsTrigger>
              <TabsTrigger value="social-media" className="rounded-lg">
                <Share2 className="h-4 w-4 mr-1" />
                Social
              </TabsTrigger>
              <TabsTrigger value="articles" className="rounded-lg">
                <Newspaper className="h-4 w-4 mr-1" />
                Articles
              </TabsTrigger>
              <TabsTrigger value="inquiries" className="rounded-lg gap-1">
                <MessageSquare className="h-4 w-4" />
                Inquiries
              </TabsTrigger>
              <TabsTrigger value="cms" className="rounded-lg">
                <Layout className="h-4 w-4 mr-1" />
                CMS
              </TabsTrigger>
              <TabsTrigger value="auctions" className="rounded-lg gap-1">
                <Gavel className="h-4 w-4" /> Auctions
              </TabsTrigger>
              <TabsTrigger value="framing" className="rounded-lg gap-1">
                <Frame className="h-4 w-4" /> Framing
              </TabsTrigger>
              <TabsTrigger value="certificates" className="rounded-lg">
                <Shield className="h-4 w-4 mr-1" />
                Certificates
              </TabsTrigger>
              <TabsTrigger value="ai-assistant" className="rounded-lg">
                <Bot className="h-4 w-4 mr-1" />
                AI
              </TabsTrigger>
              <TabsTrigger value="site-settings" className="rounded-lg gap-1">
                <Settings2 className="h-4 w-4" /> Site Settings
              </TabsTrigger>
              <TabsTrigger value="settings" className="rounded-lg">
                <Settings className="h-4 w-4" /> Commission
              </TabsTrigger>
              <TabsTrigger value="quickstart" className="rounded-lg gap-1 text-amber-600">
                <Rocket className="h-4 w-4" /> Setup
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab - Enhanced Dashboard */}
            <TabsContent value="overview">
              <OverviewDashboard token={token} onNavigate={setActiveTab} />
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              <AnalyticsDashboard token={token} />
            </TabsContent>

            {/* Products Tab */}
            <TabsContent value="products">
              <ProductsTab products={products} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Dynamic Pricing Tab */}
            <TabsContent value="pricing">
              <DynamicPricingTab products={products} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Auctions Tab */}
            <TabsContent value="auctions">
              <AuctionsTab token={token} />
            </TabsContent>

            {/* Marketing Tab */}
            <TabsContent value="marketing">
              <MarketingTab token={token} />
            </TabsContent>

            {/* Social Media Tab */}
            <TabsContent value="social-media">
              <SocialMediaTab token={token} />
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles">
              <ArticlesTab articles={articles} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Inquiries Tab */}
            <TabsContent value="inquiries">
              <InquiriesTab token={token} />
            </TabsContent>

            {/* CMS Tab */}
            <TabsContent value="cms">
              <div className="space-y-6">
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Layout className="h-5 w-5" />
                      Content Management System
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Edit text content across your website. Changes are saved immediately.
                    </p>
                  </CardHeader>
                </Card>

                {/* Homepage Content */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Globe className="h-5 w-5 text-primary" />
                      Homepage Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {cmsContent.homepage && Object.entries(cmsContent.homepage).map(([section, data]) => (
                      <div key={section} className="p-4 bg-muted/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-medium capitalize">{section.replace(/_/g, ' ')}</Label>
                          <div className="flex gap-2">
                            {cmsEditMode[`homepage_${section}`] ? (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => cancelCmsEdit('homepage', section)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => saveCmsContent('homepage', section)}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => startCmsEdit('homepage', section, data.content)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {!data.is_default && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => resetCmsContent('homepage', section)}
                                    className="text-muted-foreground"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {cmsEditMode[`homepage_${section}`] ? (
                          <Textarea
                            value={cmsEditValues[`homepage_${section}`] || ""}
                            onChange={(e) => setCmsEditValues({
                              ...cmsEditValues,
                              [`homepage_${section}`]: e.target.value
                            })}
                            rows={section.includes('description') ? 4 : 2}
                            className="mt-2"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">{data.content}</p>
                        )}
                        {data.is_default && (
                          <Badge variant="outline" className="mt-2 text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Artist Page Content */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-primary" />
                      Artist Page Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {cmsContent.artist && Object.entries(cmsContent.artist).map(([section, data]) => (
                      <div key={section} className="p-4 bg-muted/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-medium capitalize">{section.replace(/_/g, ' ')}</Label>
                          <div className="flex gap-2">
                            {cmsEditMode[`artist_${section}`] ? (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => cancelCmsEdit('artist', section)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => saveCmsContent('artist', section)}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => startCmsEdit('artist', section, data.content)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {!data.is_default && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => resetCmsContent('artist', section)}
                                    className="text-muted-foreground"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {cmsEditMode[`artist_${section}`] ? (
                          <Textarea
                            value={cmsEditValues[`artist_${section}`] || ""}
                            onChange={(e) => setCmsEditValues({
                              ...cmsEditValues,
                              [`artist_${section}`]: e.target.value
                            })}
                            rows={section === 'bio' ? 6 : 2}
                            className="mt-2"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">{data.content}</p>
                        )}
                        {data.is_default && (
                          <Badge variant="outline" className="mt-2 text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>

                {/* Footer Content */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Type className="h-5 w-5 text-primary" />
                      Footer Content
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {cmsContent.footer && Object.entries(cmsContent.footer).map(([section, data]) => (
                      <div key={section} className="p-4 bg-muted/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <Label className="font-medium capitalize">{section.replace(/_/g, ' ')}</Label>
                          <div className="flex gap-2">
                            {cmsEditMode[`footer_${section}`] ? (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => cancelCmsEdit('footer', section)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                                <Button 
                                  size="sm"
                                  onClick={() => saveCmsContent('footer', section)}
                                >
                                  <Save className="h-4 w-4 mr-1" />
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => startCmsEdit('footer', section, data.content)}
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                                {!data.is_default && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => resetCmsContent('footer', section)}
                                    className="text-muted-foreground"
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {cmsEditMode[`footer_${section}`] ? (
                          <Textarea
                            value={cmsEditValues[`footer_${section}`] || ""}
                            onChange={(e) => setCmsEditValues({
                              ...cmsEditValues,
                              [`footer_${section}`]: e.target.value
                            })}
                            rows={2}
                            className="mt-2"
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground">{data.content}</p>
                        )}
                        {data.is_default && (
                          <Badge variant="outline" className="mt-2 text-xs">Default</Badge>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Orders Tab */}
            {/* Orders Tab */}
            <TabsContent value="orders">
              <OrdersTab orders={orders} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Quote Requests Tab */}
            <TabsContent value="quotes">
              <Card className="rounded-2xl">
                <CardHeader>
                  <CardTitle>Wholesale Quote Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {quoteRequests.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No quote requests yet</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Business</TableHead>
                          <TableHead>Contact</TableHead>
                          <TableHead>Products</TableHead>
                          <TableHead>Est. Value</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {quoteRequests.map((quote) => (
                          <TableRow key={quote.quote_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{quote.business_name}</p>
                                <p className="text-xs text-muted-foreground">{quote.business_type}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p>{quote.contact_name}</p>
                                <p className="text-xs text-muted-foreground">{quote.email}</p>
                              </div>
                            </TableCell>
                            <TableCell>{quote.items?.length || 0} items</TableCell>
                            <TableCell>{formatPrice(quote.estimated_total)}</TableCell>
                            <TableCell>
                              <Badge variant={
                                quote.status === "pending" ? "secondary" :
                                quote.status === "responded" ? "default" : "outline"
                              }>
                                {quote.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{formatDate(quote.created_at)}</TableCell>
                            <TableCell>
                              {quote.status === "pending" && (
                                <Button size="sm" onClick={() => openQuoteResponse(quote)} className="rounded-full">
                                  <Send className="h-4 w-4 mr-1" /> Respond
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Wholesale Applications Tab */}
            <TabsContent value="wholesale">
              <WholesaleTab applications={wholesaleApps} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Site Settings Tab */}
            <TabsContent value="site-settings">
              <SiteSettingsTab token={token} />
            </TabsContent>

            {/* Quick Start Guide Tab */}
            <TabsContent value="quickstart">
              <QuickStartGuide token={token} />
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <UsersManagementTab users={users} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Sales Agents Tab */}
            <TabsContent value="agents">
              <SalesAgentsTab agents={salesAgents} token={token} onRefresh={fetchData} />
            </TabsContent>

            {/* Promoters/Ambassadors Tab - Using new management component */}
            <TabsContent value="promoters">
              <AmbassadorManagementTab token={token} />
            </TabsContent>

            {/* Framing Tab - Frame Builder and Pricing */}
            <TabsContent value="framing">
              <Tabs defaultValue="builder" className="space-y-6">
                <TabsList>
                  <TabsTrigger value="builder">Frame Builder</TabsTrigger>
                  <TabsTrigger value="pricing">Pricing Management</TabsTrigger>
                </TabsList>
                
                <TabsContent value="builder">
                  <FrameBuilderTab token={token} />
                </TabsContent>
                
                <TabsContent value="pricing">
                  <FramePricingTab token={token} />
                </TabsContent>
              </Tabs>
            </TabsContent>

            {/* Settings Tab - Commission Configuration */}
            <TabsContent value="settings">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Commission Settings */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Percent className="h-5 w-5" />
                      Sales Agent Commission Rates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { category: "original", label: "Original Artwork", defaultRate: 10 },
                      { category: "limited_edition", label: "Limited Edition Prints", defaultRate: 7 },
                      { category: "print", label: "Standard Prints", defaultRate: 5 },
                      { category: "merchandise", label: "Merchandise/Apparel", defaultRate: 5 },
                      { category: "reorder", label: "Reorders (Ongoing)", defaultRate: 3 },
                    ].map((item) => {
                      const setting = commissionSettings.find(s => s.category === item.category);
                      const currentRate = setting?.rate ? (setting.rate * 100) : item.defaultRate;
                      return (
                        <div key={item.category} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                          <div>
                            <p className="font-medium">{item.label}</p>
                            <p className="text-xs text-muted-foreground">
                              {setting?.duration_months ? `${setting.duration_months} months` : 
                               setting?.one_time ? "One-time" : "Ongoing"}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Select
                              value={currentRate.toString()}
                              onValueChange={(v) => handleUpdateCommission(item.category, parseFloat(v) / 100)}
                            >
                              <SelectTrigger className="w-24 rounded-lg">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[3, 5, 7, 10, 12, 15, 20].map((rate) => (
                                  <SelectItem key={rate} value={rate.toString()}>{rate}%</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>

                {/* Agent Tier Settings */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Award className="h-5 w-5" />
                      Sales Agent Tiers
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {[
                      { name: "Starter", min: 0, max: 10, commission: "5%" },
                      { name: "Pro", min: 10, max: 25, commission: "7%" },
                      { name: "Elite", min: 25, max: "∞", commission: "10%" },
                    ].map((tier) => (
                      <div key={tier.name} className="p-4 bg-muted/30 rounded-xl">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium">{tier.name}</h4>
                          <Badge>{tier.commission} base</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {tier.min}-{tier.max} linked accounts required
                        </p>
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground mt-4">
                      * Tier settings are currently fixed. Contact support to modify tier thresholds.
                    </p>
                  </CardContent>
                </Card>

                {/* Promoter Commission Settings */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="h-5 w-5" />
                      Promoter/Ambassador Commission
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Referral Commission</h4>
                        <Badge>10%</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Commission on first purchase from referred customers
                      </p>
                    </div>
                    <div className="p-4 bg-muted/30 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium">Repeat Purchase</h4>
                        <Badge variant="outline">5%</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Commission on subsequent purchases (12 months)
                      </p>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Stats */}
                <Card className="rounded-2xl">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="h-5 w-5" />
                      Program Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-primary/5 rounded-xl text-center">
                        <p className="text-3xl font-bold text-primary">{salesAgents.filter(a => a.status === "active").length}</p>
                        <p className="text-sm text-muted-foreground">Active Agents</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl text-center">
                        <p className="text-3xl font-bold text-green-600">{promoters.length}</p>
                        <p className="text-sm text-muted-foreground">Active Promoters</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl text-center">
                        <p className="text-3xl font-bold text-blue-600">
                          {salesAgents.reduce((sum, a) => sum + (a.total_accounts || 0), 0)}
                        </p>
                        <p className="text-sm text-muted-foreground">Agent Linked Accounts</p>
                      </div>
                      <div className="p-4 bg-amber-50 rounded-xl text-center">
                        <p className="text-3xl font-bold text-amber-600">
                          {formatPrice(salesAgents.reduce((sum, a) => sum + (a.total_sales || 0), 0))}
                        </p>
                        <p className="text-sm text-muted-foreground">Agent Total Sales</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Certificates Tab */}
            <TabsContent value="certificates">
              <CertificatesTab token={token} />
            </TabsContent>

            {/* AI Assistant Tab */}
            <TabsContent value="ai-assistant">
              <Card className="rounded-2xl">
                <CardContent className="pt-6">
                  <AIAssistant token={token} />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* AI Floating Widget */}
      <AIFloatingWidget token={token} />

      {/* Welcome Modal for first-time admins */}
      <WelcomeModal 
        user={user} 
        onClose={(tab) => {
          if (tab) setActiveTab(tab);
        }} 
      />

      {/* Territory Assignment Dialog */}
      <Dialog open={territoryDialogOpen} onOpenChange={setTerritoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Territory</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Territory/Region</Label>
              <Select
                value={territoryForm.territory}
                onValueChange={(v) => setTerritoryForm({ ...territoryForm, territory: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NSW">New South Wales</SelectItem>
                  <SelectItem value="VIC">Victoria</SelectItem>
                  <SelectItem value="QLD">Queensland</SelectItem>
                  <SelectItem value="WA">Western Australia</SelectItem>
                  <SelectItem value="SA">South Australia</SelectItem>
                  <SelectItem value="TAS">Tasmania</SelectItem>
                  <SelectItem value="NT">Northern Territory</SelectItem>
                  <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                  <SelectItem value="NZ">New Zealand</SelectItem>
                  <SelectItem value="INTL">International</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Postcodes (comma-separated, optional)</Label>
              <Input
                className="mt-1"
                placeholder="e.g., 2000, 2001, 2010"
                value={territoryForm.postcodes}
                onChange={(e) => setTerritoryForm({ ...territoryForm, postcodes: e.target.value })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Leave empty for entire region
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTerritoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignTerritory}>
              Assign Territory
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Editor Dialog */}
      <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input
                  value={productForm.title}
                  onChange={(e) => setProductForm({ ...productForm, title: e.target.value })}
                  placeholder="Artwork title"
                />
              </div>
              <div className="col-span-2">
                <Label>Description</Label>
                <Textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  placeholder="Describe the artwork..."
                  rows={3}
                />
              </div>
              <div>
                <Label>Base Price (AUD)</Label>
                <Input
                  type="number"
                  value={productForm.base_price}
                  onChange={(e) => setProductForm({ ...productForm, base_price: e.target.value })}
                  placeholder="99.00"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={productForm.category} onValueChange={(v) => setProductForm({ ...productForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="limited_edition">Limited Edition</SelectItem>
                    <SelectItem value="original">Original</SelectItem>
                    <SelectItem value="print">Print</SelectItem>
                    <SelectItem value="apparel">Apparel</SelectItem>
                    <SelectItem value="merchandise">Merchandise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Stock</Label>
                <Input
                  type="number"
                  value={productForm.stock}
                  onChange={(e) => setProductForm({ ...productForm, stock: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Min Wholesale Qty</Label>
                <Input
                  type="number"
                  value={productForm.wholesale_min_qty}
                  onChange={(e) => setProductForm({ ...productForm, wholesale_min_qty: parseInt(e.target.value) })}
                />
              </div>
            </div>

            {/* Toggles */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_limited_edition}
                  onCheckedChange={(v) => setProductForm({ ...productForm, is_limited_edition: v })}
                />
                <Label>Limited Edition</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.is_featured}
                  onCheckedChange={(v) => setProductForm({ ...productForm, is_featured: v })}
                />
                <Label>Featured</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={productForm.has_variants}
                  onCheckedChange={(v) => setProductForm({ ...productForm, has_variants: v })}
                />
                <Label>Has Size Variants</Label>
              </div>
            </div>

            {/* Images */}
            {/* Product Images - Upload Component */}
            <div>
              <Label className="mb-3 block">Product Images</Label>
              <MultiImageUpload
                images={productForm.images.filter(img => img.trim())}
                onChange={(newImages) => setProductForm({ ...productForm, images: newImages })}
                maxImages={10}
              />
              <p className="text-xs text-muted-foreground mt-2">
                Or paste URLs directly:
              </p>
              <div className="mt-2 space-y-2">
                {productForm.images.map((img, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={img}
                      onChange={(e) => updateImage(idx, e.target.value)}
                      placeholder="https://..."
                      className="flex-1 text-xs"
                    />
                    {productForm.images.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" onClick={() => removeImage(idx)}>
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" onClick={addImageField} className="w-full">
                  <Plus className="h-4 w-4 mr-1" /> Add URL
                </Button>
              </div>
            </div>

            {/* Variants */}
            {productForm.has_variants && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Size Variants</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addVariant}>
                    <Plus className="h-4 w-4 mr-1" /> Add Size
                  </Button>
                </div>
                <div className="space-y-3">
                  {productForm.variants.map((variant, idx) => (
                    <div key={idx} className="grid grid-cols-6 gap-2 p-3 bg-muted/50 rounded-lg items-end">
                      <div>
                        <Label className="text-xs">Name</Label>
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(idx, "name", e.target.value)}
                          placeholder="Small"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Size</Label>
                        <Input
                          value={variant.size}
                          onChange={(e) => updateVariant(idx, "size", e.target.value)}
                          placeholder="A4"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Price</Label>
                        <Input
                          type="number"
                          value={variant.price}
                          onChange={(e) => updateVariant(idx, "price", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Stock</Label>
                        <Input
                          type="number"
                          value={variant.stock}
                          onChange={(e) => updateVariant(idx, "stock", e.target.value)}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Min WS Qty</Label>
                        <Input
                          type="number"
                          value={variant.wholesale_min_qty}
                          onChange={(e) => updateVariant(idx, "wholesale_min_qty", e.target.value)}
                        />
                      </div>
                      <div>
                        {productForm.variants.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeVariant(idx)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleProductSubmit}>
              {editingProduct ? "Update Product" : "Create Product"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quote Response Dialog */}
      <Dialog open={quoteResponseOpen} onOpenChange={setQuoteResponseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Respond to Quote Request</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-muted rounded-lg">
                <p className="font-medium">{selectedQuote.business_name}</p>
                <p className="text-sm text-muted-foreground">{selectedQuote.contact_name} • {selectedQuote.email}</p>
                <p className="text-sm mt-2">Estimated Value: {formatPrice(selectedQuote.estimated_total)}</p>
              </div>
              
              <div>
                <Label>Discount Offer (%)</Label>
                <Select 
                  value={quoteResponse.discount_percent.toString()} 
                  onValueChange={(v) => setQuoteResponse({ ...quoteResponse, discount_percent: parseInt(v) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 15, 20, 25, 30, 35, 40].map((d) => (
                      <SelectItem key={d} value={d.toString()}>{d}% off</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Message (optional)</Label>
                <Textarea
                  value={quoteResponse.message}
                  onChange={(e) => setQuoteResponse({ ...quoteResponse, message: e.target.value })}
                  placeholder="Add a personal message..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuoteResponseOpen(false)}>Cancel</Button>
            <Button onClick={sendQuoteResponse}>
              <Send className="h-4 w-4 mr-2" /> Send Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Article Editor Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingArticle ? "Edit Article" : "Create New Article"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Title */}
            <div>
              <Label>Title *</Label>
              <Input
                className="mt-1"
                value={articleForm.title}
                onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })}
                placeholder="Article title..."
              />
            </div>

            {/* Excerpt */}
            <div>
              <Label>Excerpt/Summary *</Label>
              <Textarea
                className="mt-1"
                rows={2}
                value={articleForm.excerpt}
                onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })}
                placeholder="Brief summary for article cards..."
              />
            </div>

            {/* Content */}
            <div>
              <Label>Content * (Markdown supported)</Label>
              <Textarea
                className="mt-1 font-mono text-sm"
                rows={12}
                value={articleForm.content}
                onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })}
                placeholder="Full article content... Use ### for headings, **bold**, *italic*, - for lists"
              />
            </div>

            {/* Category and Featured Image */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={articleForm.category}
                  onValueChange={(v) => setArticleForm({ ...articleForm, category: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="news">News & Updates</SelectItem>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="culture">Culture</SelectItem>
                    <SelectItem value="art4health">ART4HEALTH</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Featured Image */}
            <div>
              <Label className="mb-2 block">Featured Image</Label>
              <ImageUpload
                value={articleForm.featured_image}
                onChange={(url) => setArticleForm({ ...articleForm, featured_image: url })}
                onRemove={() => setArticleForm({ ...articleForm, featured_image: "" })}
                label="Upload Featured Image"
              />
              <p className="text-xs text-muted-foreground mt-2">Or paste URL:</p>
              <Input
                className="mt-1"
                value={articleForm.featured_image}
                onChange={(e) => setArticleForm({ ...articleForm, featured_image: e.target.value })}
                placeholder="https://..."
              />
            </div>

            {/* Tags */}
            <div>
              <Label>Tags (comma-separated)</Label>
              <Input
                className="mt-1"
                value={articleForm.tags}
                onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })}
                placeholder="art, culture, investment, collecting"
              />
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-2">
                <Switch
                  checked={articleForm.is_published}
                  onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_published: checked })}
                />
                <Label>Published</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={articleForm.is_featured}
                  onCheckedChange={(checked) => setArticleForm({ ...articleForm, is_featured: checked })}
                />
                <Label>Featured on Media page</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setArticleDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleArticleSubmit}>
              {editingArticle ? "Update Article" : "Create Article"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboardPage;
